import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, ReplaySubject, switchMap, take, tap } from 'rxjs';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { environment } from 'environments/environment';

@Injectable({
    providedIn: 'root'
})
export class NotificationsService
{
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient)
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]>
    {
        return this._notifications.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications
     */
    getAll(): Observable<Notification[]>
    {
        return this._httpClient.get<any>(`${environment.baseUrl}notifications`).pipe(
            map(response => {
                const notifications = response.data.map(item => {
                    return {
                        id         : item.id,
                        icon       : this._mapIcon(item.icon),
                        title      : item.title,
                        description: item.message,
                        time       : item.created_at,
                        link       : item.link,
                        useRouter  : true,
                        read       : !!item.read_at
                    };
                });
                return notifications;
            }),
            tap((notifications) => {
                this._notifications.next(notifications);
            }),
            catchError((error) => {
                console.error('Error loading notifications:', error);
                const emptyNotifications: Notification[] = [];
                this._notifications.next(emptyNotifications);
                return of(emptyNotifications);
            })
        );
    }

    private _mapIcon(icon: string): string
    {
        const iconMapping = {
            'assignment': 'heroicons_outline:clipboard-list',
            'school'    : 'heroicons_outline:academic-cap',
            'flag'      : 'heroicons_outline:flag',
            'notifications': 'heroicons_outline:bell',
            'alarm'     : 'heroicons_outline:clock',
            'mail'      : 'heroicons_outline:mail',
            'check_circle': 'heroicons_outline:check-circle',
            'waving_hand': 'heroicons_outline:hand',
            'person': 'heroicons_outline:user',
            'info': 'heroicons_outline:information-circle',
            'warning': 'heroicons_outline:exclamation',
            'error': 'heroicons_outline:exclamation-circle'
        };

        return iconMapping[icon] || 'heroicons_outline:bell';
    }

    /**
     * Create a notification
     *
     * @param notification
     */
    create(notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.post<Notification>('api/v1/notifications', {notification}).pipe(
                map((newNotification) => {

                    // Update the notifications with the new notification
                    this._notifications.next([...notifications, newNotification]);

                    // Return the new notification from observable
                    return newNotification;
                })
            ))
        );
    }

    /**
     * Update the notification
     *
     * @param id
     * @param notification
     */
    update(id: string, notification: Notification): Observable<Notification>
    {
        const url = notification.read 
            ? `api/v1/notifications/${id}/mark-read`
            : `api/v1/notifications/${id}/mark-unread`;

        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.post<any>(url, {}).pipe(
                map(() => {

                    // Find the index of the updated notification
                    const index = notifications.findIndex(item => item.id === id);

                    // Update the notification
                    notifications[index] = notification;

                    // Update the notifications
                    this._notifications.next(notifications);

                    // Return the updated notification
                    return notification;
                })
            ))
        );
    }

    /**
     * Delete the notification
     *
     * @param id
     */
    delete(id: string): Observable<boolean>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.delete<boolean>(`api/v1/notifications/${id}`).pipe(
                map((isDeleted: boolean) => {

                    // Find the index of the deleted notification
                    const index = notifications.findIndex(item => item.id === id);

                    // Delete the notification
                    notifications.splice(index, 1);

                    // Update the notifications
                    this._notifications.next(notifications);

                    // Return the deleted status
                    return isDeleted;
                })
            ))
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.post<boolean>('api/v1/notifications/mark-all-read', {}).pipe(
                map((isUpdated: boolean) => {

                    // Go through all notifications and set them as read
                    notifications.forEach((notification, index) => {
                        notifications[index].read = true;
                    });

                    // Update the notifications
                    this._notifications.next(notifications);

                    // Return the updated status
                    return isUpdated;
                })
            ))
        );
    }
}
